/**
 * Cached manifest data (shared between all TexturePicker instances)
 */
let cachedManifest = null;
let manifestPromise = null;

/**
 * TexturePicker - A reusable texture selection widget with category dropdown and grid
 *
 * Usage:
 *   const picker = new TexturePicker({
 *     onSelect: (texture) => console.log(texture.path)
 *   });
 *   await picker.loadManifest();
 *   picker.render(container);
 */
export default class TexturePicker {
  /**
   * @param {Object} options
   * @param {Function} options.onSelect - Callback when texture selected: (texture) => void
   * @param {boolean} options.showLabels - Show texture labels under thumbnails (default: false)
   * @param {string} options.selectedPath - Initial selected texture path
   */
  constructor(options = {}) {
    this.onSelect = options.onSelect;
    this.showLabels = options.showLabels || false;
    this.selectedPath = options.selectedPath || null;

    this.container = null;
    this.manifest = null;
    this.currentCategoryId = null;
    this.textureElements = new Map();

    this._categorySelect = null;
    this._grid = null;
  }

  /**
   * Load the texture manifest (cached globally)
   */
  async loadManifest() {
    if (cachedManifest) {
      this.manifest = cachedManifest;
      return this.manifest;
    }

    if (manifestPromise) {
      this.manifest = await manifestPromise;
      return this.manifest;
    }

    manifestPromise = fetch('textures/texture-manifest.json')
      .then(res => {
        if (!res.ok) throw new Error('Failed to load texture manifest');
        return res.json();
      })
      .then(data => {
        cachedManifest = data;
        return data;
      });

    this.manifest = await manifestPromise;
    return this.manifest;
  }

  /**
   * Render the texture picker into a container
   * @param {HTMLElement} container - Parent element to render into
   */
  render(container) {
    this.container = document.createElement('div');
    this.container.className = 'texture-picker';

    if (!this.manifest) {
      this.container.textContent = 'Loading textures...';
      container.appendChild(this.container);
      return;
    }

    // Category dropdown
    const row = document.createElement('div');
    row.className = 'gui-row gui-row-stacked';

    const label = document.createElement('label');
    label.className = 'gui-label gui-label-stacked';
    label.textContent = 'Category';
    row.appendChild(label);

    this._categorySelect = document.createElement('select');
    this._categorySelect.className = 'gui-select';
    this.manifest.categories.forEach(cat => {
      const option = document.createElement('option');
      option.value = cat.id;
      option.textContent = `${cat.label} (${cat.textures.length})`;
      this._categorySelect.appendChild(option);
    });
    row.appendChild(this._categorySelect);
    this.container.appendChild(row);

    // Scrollable grid container
    const gridContainer = document.createElement('div');
    gridContainer.className = 'texture-picker-grid-container';
    this._grid = document.createElement('div');
    this._grid.className = 'texture-picker-grid';
    gridContainer.appendChild(this._grid);
    this.container.appendChild(gridContainer);

    // Category change handler
    this._categorySelect.addEventListener('change', () => this._onCategoryChange());

    // Set initial category (matching selectedPath if possible)
    const initialCategoryId = this._findCategoryForPath(this.selectedPath);
    if (initialCategoryId) {
      this._categorySelect.value = initialCategoryId;
    }
    this._onCategoryChange();

    container.appendChild(this.container);
  }

  /**
   * Find which category contains a texture path
   * @param {string} path - Texture path to search for
   * @returns {string|null} Category ID or null
   */
  _findCategoryForPath(path) {
    if (!path || !this.manifest) return null;
    for (const category of this.manifest.categories) {
      if (category.textures.some(tex => tex.path === path)) {
        return category.id;
      }
    }
    return null;
  }

  /**
   * Update the grid when category changes
   * @private
   */
  _onCategoryChange() {
    if (!this.manifest) return;

    const categoryId = this._categorySelect.value;
    this.currentCategoryId = categoryId;

    const category = this.manifest.categories.find(c => c.id === categoryId);
    if (!category) return;

    // Clear and rebuild grid
    this.textureElements.clear();
    this._grid.innerHTML = '';

    category.textures.forEach(texture => {
      const item = this.createTextureItem(texture);
      this._grid.appendChild(item);
    });

    // Re-apply selection highlight if selectedPath is in this category
    if (this.selectedPath) {
      const el = this.textureElements.get(this.selectedPath);
      if (el) {
        el.classList.add('active');
      }
    }
  }

  /**
   * Create a texture thumbnail item
   * @param {Object} texture - Texture data from manifest
   * @returns {HTMLElement}
   */
  createTextureItem(texture) {
    const item = document.createElement('div');
    item.className = 'texture-picker-item';
    item.title = texture.label;
    item.dataset.path = texture.path;

    const img = document.createElement('img');
    img.src = texture.thumbnail;
    img.alt = texture.label;
    img.loading = 'lazy';
    img.draggable = false;
    item.appendChild(img);

    if (this.showLabels) {
      const label = document.createElement('span');
      label.className = 'texture-picker-label';
      label.textContent = texture.label;
      item.appendChild(label);
    }

    item.addEventListener('click', () => {
      this.selectTexture(texture, item);
    });

    this.textureElements.set(texture.path, item);
    return item;
  }

  /**
   * Handle texture selection
   * @param {Object} texture - Selected texture
   * @param {HTMLElement} element - Clicked element
   */
  selectTexture(texture, element) {
    // Clear previous selection
    this.textureElements.forEach(el => el.classList.remove('active'));

    // Set new selection
    element.classList.add('active');
    this.selectedPath = texture.path;

    if (this.onSelect) {
      this.onSelect(texture);
    }
  }

  /**
   * Programmatically set selection by path (auto-switches category)
   * @param {string} path - Texture path to select
   */
  setSelection(path) {
    this.selectedPath = path;

    if (!this.manifest || !this._categorySelect) return;

    // Switch to the category containing this path
    const categoryId = this._findCategoryForPath(path);
    if (categoryId && categoryId !== this.currentCategoryId) {
      this._categorySelect.value = categoryId;
      this._onCategoryChange();
      return; // _onCategoryChange already applies the highlight
    }

    // Highlight the matching element in current grid
    this.textureElements.forEach((el, elPath) => {
      if (elPath === path) {
        el.classList.add('active');
      } else {
        el.classList.remove('active');
      }
    });
  }

  /**
   * Get currently selected texture path
   * @returns {string|null}
   */
  getSelection() {
    return this.selectedPath;
  }

  /**
   * Clear all resources
   */
  destroy() {
    this.textureElements.clear();
    this._categorySelect = null;
    this._grid = null;
    this.currentCategoryId = null;
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
    this.container = null;
  }
}
